import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { adminTldCatalogService } from "@/services/admin-tld-catalog.service";
import type {
  CreateTldCatalogRequest,
  UpdateTldCatalogRequest,
} from "@/types/domain";

/**
 * Query keys for TLD catalog admin queries.
 *
 * - ['admin', 'tld-catalog'] — all TLD catalog queries
 * - ['admin', 'tld-catalog', 'list'] — the catalog list
 */
export const tldCatalogKeys = {
  all: ["admin", "tld-catalog"] as const,
  lists: () => [...tldCatalogKeys.all, "list"] as const,
};

// ============================================================================
// Queries
// ============================================================================

/**
 * Get all TLD catalog entries.
 *
 * @returns The full TLD catalog (not paginated)
 */
export function useAdminTldCatalog() {
  return useQuery({
    queryKey: tldCatalogKeys.lists(),
    queryFn: () => adminTldCatalogService.getAll(),
    staleTime: 30_000,
    gcTime: 5 * 60_000,
  });
}

// ============================================================================
// Mutations
// ============================================================================

/**
 * Create a new TLD catalog entry.
 *
 * Invalidates the TLD catalog list.
 */
export function useCreateTldCatalog() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateTldCatalogRequest) =>
      adminTldCatalogService.create(data),
    onSuccess: (tld) => {
      queryClient.invalidateQueries({ queryKey: tldCatalogKeys.lists() });
      toast.success("TLD created successfully", {
        description: `${tld.tld} has been added to the catalog`,
      });
    },
    onError: (error: Error) => {
      toast.error("Failed to create TLD", {
        description: error.message,
      });
    },
  });
}

/**
 * Update an existing TLD catalog entry.
 *
 * Invalidates the TLD catalog list.
 */
export function useUpdateTldCatalog() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateTldCatalogRequest }) =>
      adminTldCatalogService.update(id, data),
    onSuccess: (updatedTld) => {
      queryClient.invalidateQueries({ queryKey: tldCatalogKeys.lists() });
      toast.success("TLD updated successfully", {
        description: `${updatedTld.tld} has been updated`,
      });
    },
    onError: (error: Error) => {
      toast.error("Failed to update TLD", {
        description: error.message,
      });
    },
  });
}

/**
 * Enable or disable a TLD catalog entry.
 *
 * Invalidates the TLD catalog list.
 */
export function useSetTldEnabled() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, isEnabled }: { id: string; isEnabled: boolean }) =>
      adminTldCatalogService.setEnabled(id, isEnabled),
    onSuccess: (_response, { isEnabled }) => {
      queryClient.invalidateQueries({ queryKey: tldCatalogKeys.lists() });
      toast.success(`TLD ${isEnabled ? "enabled" : "disabled"}`);
    },
    onError: (error: Error) => {
      queryClient.invalidateQueries({ queryKey: tldCatalogKeys.lists() });
      toast.error("Failed to update TLD status", {
        description: error.message,
      });
    },
  });
}
